import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, GripVertical, Trash2, Zap, ArrowUp, ArrowDown, School, Loader2, Save, Users } from "lucide-react";
import LeafletMap from "@/components/LeafletMap";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import POIAutocomplete from "@/components/POIAutocomplete";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  name: "",
  type: "pickup",
  date: today(),
  category: "regular",
  round_trip: false,
  school_address: "",
  school_lat: null,
  school_lng: null,
  stops: [],
};

function SortableStop({ stop, index, onRemove, onMoveUp, onMoveDown, isFirst, isLast, total }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop._key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const kids = stop.children_ids?.length || 0;
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`stop-item-${index}`}
      className={`flex items-center gap-2 p-2 border-b border-zinc-200 bg-white ${isDragging ? "shadow-lg" : ""}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-700 px-1"
        data-testid={`stop-drag-${index}`}
        {...attributes}
        {...listeners}
        title="Glisser-déposer pour réordonner"
      >
        <GripVertical size={14} />
      </button>
      <span className={`w-7 h-7 flex items-center justify-center text-xs font-bold border border-zinc-900 ${
        isFirst ? "bg-emerald-500 text-white" : isLast ? "bg-red-500 text-white" : "bg-[#FFC60A]"
      }`} title={isFirst ? "Départ" : isLast ? "Arrivée" : `Arrêt ${index + 1}`}>
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate" title={stop.display_name || stop.address}>
          {stop.display_name || stop.address || "Adresse manquante"}
        </div>
        <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-2 mt-0.5">
          <span className="inline-flex items-center gap-1">
            <Users size={10} /> {kids} passager{kids > 1 ? "s" : ""}
          </span>
          {stop.scheduled_time && (
            <span>· Heure estimée {stop.scheduled_time}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" data-testid={`stop-up-${index}`}
          onClick={() => onMoveUp(index)} disabled={isFirst}
          title="Monter"
          className="p-1 border border-zinc-300 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-zinc-900">
          <ArrowUp size={11} />
        </button>
        <button type="button" data-testid={`stop-down-${index}`}
          onClick={() => onMoveDown(index)} disabled={isLast}
          title="Descendre"
          className="p-1 border border-zinc-300 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-zinc-900">
          <ArrowDown size={11} />
        </button>
        <button type="button" data-testid={`stop-remove-${index}`}
          onClick={() => onRemove(index)}
          title="Supprimer cet arrêt"
          className="p-1 border border-zinc-300 hover:bg-red-600 hover:text-white">
          <Trash2 size={11} />
        </button>
      </div>
      <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 w-10 text-right">
        {isFirst ? "Départ" : isLast && total > 1 ? "Arrivée" : ""}
      </span>
    </li>
  );
}

export default function RouteEditorModal({ initial, children: kids, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => initial ? {
    ...emptyForm, ...initial,
    school_address: initial.school_address || "",
    school_lat: initial.school_lat || null,
    school_lng: initial.school_lng || null,
    stops: (initial.stops || []).map((s, i) => ({
      ...s,
      _key: s.id || `s-${i}-${Date.now()}`,
      display_name: s.display_name || s.address || "",
    })),
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [shapeCoords, setShapeCoords] = useState([]);
  const [stopInput, setStopInput] = useState("");
  const [addingTime, setAddingTime] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const waypoints = useMemo(() => {
    const wps = [];
    if (form.school_lat && form.school_lng) {
      wps.push({ lat: form.school_lat, lng: form.school_lng, address: `🏫 ${form.school_address || "École"}` });
    }
    form.stops.forEach((s, i) => {
      if (s.lat && (s.lng || s.lon)) {
        wps.push({ lat: s.lat, lng: s.lng || s.lon, address: `📍 ${i + 1}. ${s.display_name || s.address}` });
      }
    });
    return wps;
  }, [form.school_lat, form.school_lng, form.school_address, form.stops]);

  const refreshShape = async () => {
    if (waypoints.length < 2) { setShapeCoords([]); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/routes/preview-shape", {
        waypoints: waypoints.map((w) => ({ lat: w.lat, lng: w.lng })),
      });
      setShapeCoords(data?.coords || []);
    } catch { setShapeCoords([]); }
    finally { setPreviewing(false); }
  };

  useEffect(() => {
    const t = setTimeout(refreshShape, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(waypoints)]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const oldIdx = f.stops.findIndex((s) => s._key === active.id);
      const newIdx = f.stops.findIndex((s) => s._key === over.id);
      return { ...f, stops: arrayMove(f.stops, oldIdx, newIdx) };
    });
  };

  const move = (idx, delta) => {
    setForm((f) => {
      const j = idx + delta;
      if (j < 0 || j >= f.stops.length) return f;
      return { ...f, stops: arrayMove(f.stops, idx, j) };
    });
  };

  const removeStop = (idx) => {
    setForm((f) => ({ ...f, stops: f.stops.filter((_, i) => i !== idx) }));
  };

  const addStopFromAutocomplete = (sel) => {
    setForm((f) => ({
      ...f,
      stops: [...f.stops, {
        _key: `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        address: sel.display_name, display_name: sel.display_name,
        lat: sel.lat, lng: sel.lon,
        children_ids: [],
        scheduled_time: addingTime || "",
      }],
    }));
    setStopInput("");
    setAddingTime("");
  };

  const addStopFromChild = (childId) => {
    const c = kids.find((x) => x.id === childId);
    if (!c) return;
    if (c.home_lat == null || c.home_lng == null) {
      toast.error(`${c.first_name} ${c.last_name} n'a pas d'adresse géolocalisée`);
      return;
    }
    setForm((f) => ({
      ...f,
      stops: [...f.stops, {
        _key: `s-${Date.now()}-${c.id}`,
        address: c.home_address || `${c.first_name} ${c.last_name}`,
        display_name: c.home_address || `${c.first_name} ${c.last_name}`,
        lat: c.home_lat, lng: c.home_lng,
        children_ids: [c.id],
        scheduled_time: "",
      }],
    }));
  };

  const pickSchool = (sel) => {
    // ``sel`` can come from POIAutocomplete (uses stop_name/stop_lat/stop_lon)
    // or from AddressAutocomplete (uses display_name/lat/lon).
    const name = sel.stop_name || sel.display_name;
    const lat = sel.stop_lat ?? sel.lat;
    const lng = sel.stop_lon ?? sel.lon ?? sel.lng;
    setForm((f) => ({ ...f, school_address: name, school_lat: lat, school_lng: lng }));
  };

  const pickPOIStop = (sel) => {
    // POI selected from the existing transit_stops database.
    setForm((f) => ({
      ...f,
      stops: [...f.stops, {
        _key: `s-${Date.now()}-${sel.stop_id}`,
        address: sel.address || sel.stop_name,
        display_name: sel.stop_name,
        lat: sel.stop_lat,
        lng: sel.stop_lon,
        stop_id: sel.stop_id,    // remember the link so we can de-dup later
        children_ids: [],
        scheduled_time: addingTime || "",
      }],
    }));
    setStopInput("");
    setAddingTime("");
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name?.trim()) return toast.error("Donnez un nom à la tournée");
    if (!form.school_address || form.school_lat == null) return toast.error("Choisissez une école (autocomplete)");
    if (form.stops.length === 0) return toast.error("Ajoutez au moins un point de ramassage");

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        date: form.date,
        category: form.category,
        round_trip: !!form.round_trip,
        school_address: form.school_address,
        school_lat: parseFloat(form.school_lat),
        school_lng: parseFloat(form.school_lng),
        stops: form.stops.map((s) => ({
          address: s.address,
          lat: parseFloat(s.lat),
          lng: parseFloat(s.lng || s.lon),
          children_ids: s.children_ids || [],
          scheduled_time: s.scheduled_time || "",
        })),
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/routes/${initial.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/routes", payload);
        saved = data;
      }
      toast.success(isEdit ? "Tournée mise à jour" : "Tournée créée");
      onSaved?.(saved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const optimize = async () => {
    if (!isEdit) return toast.info("Enregistrez la tournée d'abord pour pouvoir l'optimiser");
    setOptimizing(true);
    try {
      const { data } = await api.post(`/routes/${initial.id}/optimize`);
      const before = data.metrics_before, after = data.metrics;
      const gainKm = before && after ? (before.total_distance_km - after.total_distance_km).toFixed(2) : 0;
      toast.success(`Tournée optimisée — ${after.total_distance_km} km (gain ${gainKm} km)`);
      setForm((f) => ({
        ...f,
        stops: (data.stops || []).map((s, i) => ({
          ...s, _key: s.id || `s-${i}-${Date.now()}`,
          display_name: s.display_name || s.address,
        })),
      }));
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" data-testid="route-editor-modal">
      <form onSubmit={save} className="bg-white border border-zinc-900 w-full max-w-7xl max-h-[95vh] flex flex-col shadow-[6px_6px_0_0_#0A0A0B]">
        {/* Header */}
        <div className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between bg-white">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{isEdit ? "Édition" : "Nouvelle tournée"}</div>
            <div className="font-display font-black text-xl tracking-tighter">{form.name || "Tournée sans nom"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} data-testid="route-close" className="p-2 hover:bg-zinc-100 text-zinc-600">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body: 2-column responsive */}
        <div className="flex-1 grid lg:grid-cols-12 min-h-0 overflow-hidden">
          {/* Left: form */}
          <aside className="lg:col-span-5 border-r border-zinc-900 overflow-y-auto p-4 space-y-4">
            <section>
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500 mb-2">Informations</div>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Nom de la tournée</label>
                  <input data-testid="route-name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex : Ramassage matin Morges Nord"
                    className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Type</label>
                    <select data-testid="route-type" value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className="mt-1 w-full border border-zinc-900 px-2 py-2 text-sm bg-white">
                      <option value="pickup">Ramassage</option>
                      <option value="dropoff">Dépose</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Date</label>
                    <input type="date" data-testid="route-date" required value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="mt-1 w-full border border-zinc-900 px-2 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Catégorie</label>
                    <select data-testid="route-category" value={form.category || "regular"}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-1 w-full border border-zinc-900 px-2 py-2 text-sm bg-white">
                      <option value="regular">Régulier</option>
                      <option value="exceptional">Exceptionnel</option>
                      <option value="extracurricular">Extrascolaire</option>
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="route-roundtrip" checked={!!form.round_trip}
                    onChange={(e) => setForm({ ...form, round_trip: e.target.checked })} />
                  Aller-retour
                </label>
              </div>
            </section>

            <section className="border-t border-zinc-200 pt-3">
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500 mb-2 flex items-center gap-1">
                <School size={11} /> École (recherche dans les POI)
              </div>
              <POIAutocomplete
                value={form.school_address}
                onChange={(v) => setForm({ ...form, school_address: v })}
                onSelect={pickSchool}
                defaultType="school"
                placeholder="Tapez le nom (ex : Beausobre) ou la ville (ex : Morges)"
                testId="school-poi-autocomplete"
                emptyHint="Aucune école dans les POI. Cliquez sur « Élargir la recherche » pour voir d'autres POI, ou utilisez la saisie d'adresse Nominatim ci-dessous."
              />
              {form.school_lat && form.school_lng && (
                <div className="mt-1 text-[10px] font-mono text-emerald-700 inline-flex items-center gap-1">
                  ✓ École sélectionnée · {form.school_lat.toFixed(4)}, {form.school_lng.toFixed(4)}
                </div>
              )}
              <details className="mt-2 group">
                <summary className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-900">
                  Adresse libre (Nominatim) — si l'école n'existe pas encore dans les POI
                </summary>
                <div className="mt-2">
                  <AddressAutocomplete
                    value={form.school_address}
                    onChange={(v) => setForm({ ...form, school_address: v })}
                    onSelect={pickSchool}
                    placeholder="Ex : Collège de Beausobre – Morges"
                    testId="school-autocomplete"
                  />
                </div>
              </details>
            </section>

            <section className="border-t border-zinc-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">Arrêts de la tournée — {form.stops.length} point{form.stops.length > 1 ? "s" : ""}</div>
              </div>

              {/* Add by POI / address / child */}
              <div className="space-y-2 mb-3">
                <div>
                  <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Ajouter depuis les POI existants</label>
                  <POIAutocomplete
                    value={stopInput}
                    onChange={setStopInput}
                    onSelect={pickPOIStop}
                    placeholder="Chercher un arrêt par nom, ville…"
                    testId="stop-poi-autocomplete"
                    emptyHint="Aucun POI ne correspond. Utilisez la saisie d'adresse libre ci-dessous pour en créer un."
                  />
                </div>
                <details className="group">
                  <summary className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-900">
                    Ou saisir une adresse libre (Nominatim)
                  </summary>
                  <div className="mt-2">
                    <AddressAutocomplete
                      value={stopInput}
                      onChange={setStopInput}
                      onSelect={addStopFromAutocomplete}
                      placeholder="Rue, ville, code postal…"
                      testId="stop-autocomplete"
                    />
                  </div>
                </details>
                <div>
                  <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Ou ajouter depuis un passager</label>
                  <select data-testid="stop-from-child" value=""
                    onChange={(e) => { if (e.target.value) { addStopFromChild(e.target.value); e.target.value = ""; } }}
                    className="mt-1 w-full border border-zinc-900 px-2 py-2 text-sm bg-white">
                    <option value="">— Choisir un passager —</option>
                    {kids.map((c) => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.grade ? `· ${c.grade}` : ""}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Stops list with drag&drop */}
              {form.stops.length === 0 ? (
                <div className="border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500" data-testid="stops-empty">
                  <Plus size={20} className="mx-auto text-zinc-300" />
                  Aucun point de ramassage. Ajoutez via l'adresse ou un passager ci-dessus.
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={form.stops.map((s) => s._key)} strategy={verticalListSortingStrategy}>
                    <ul className="border border-zinc-900" data-testid="stops-list">
                      {form.stops.map((s, i) => (
                        <SortableStop
                          key={s._key}
                          stop={s}
                          index={i}
                          isFirst={i === 0}
                          isLast={i === form.stops.length - 1}
                          total={form.stops.length}
                          onRemove={removeStop}
                          onMoveUp={(idx) => move(idx, -1)}
                          onMoveDown={(idx) => move(idx, +1)}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              )}
            </section>
          </aside>

          {/* Right: map preview */}
          <section className="lg:col-span-7 flex flex-col min-h-0 overflow-hidden bg-zinc-50">
            <div className="px-4 py-2 border-b border-zinc-900 bg-white flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">Itinéraire</div>
                <div className="font-display font-bold text-sm tracking-tight">
                  {waypoints.length} point{waypoints.length > 1 ? "s" : ""} · {form.stops.length} arrêt{form.stops.length > 1 ? "s" : ""}
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
                {previewing ? <><Loader2 size={11} className="animate-spin" /> Tracé en cours…</> : "OSRM · routes réelles"}
              </div>
            </div>
            <div className="flex-1 min-h-[300px] overflow-hidden" data-testid="route-map-preview">
              <LeafletMap
                waypoints={waypoints}
                shapeCoords={shapeCoords}
                height="100%"
                showLegend={false}
                emptyMessage={waypoints.length === 0 ? "Sélectionnez une école pour démarrer" : null}
                testId="route-builder-map"
              />
            </div>
            <div className="px-4 py-3 border-t border-zinc-900 bg-white flex-shrink-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Légende</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 border border-zinc-900 bg-zinc-900 text-[#FFC60A] text-center text-[8px] font-bold leading-[12px]">S</span>
                  École (point de départ logique)
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 border border-zinc-900 bg-emerald-500"></span>
                  Départ (1<sup>er</sup> arrêt)
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 border border-zinc-900 bg-[#FFC60A]"></span>
                  Intermédiaire
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 border border-zinc-900 bg-red-500"></span>
                  Arrivée
                </span>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-900 px-4 py-3 flex items-center justify-between gap-2 flex-wrap bg-white">
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
              Annuler
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isEdit && (
              <button type="button" onClick={optimize} disabled={optimizing || form.stops.length < 2}
                data-testid="optimize-route-btn"
                title={form.stops.length < 2 ? "Ajoutez au moins 2 arrêts pour optimiser" : "Recalculer le meilleur ordre"}
                className="px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1 disabled:opacity-50">
                {optimizing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Optimiser tournée
              </button>
            )}
            <button type="submit" data-testid="save-route-btn"
              disabled={saving}
              className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a] inline-flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {isEdit ? "Enregistrer" : "Créer la tournée"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
